// Tiny fetch wrapper for the marketplace + auth APIs.
// Sends httpOnly cookies via credentials: 'include'.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

async function request(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    throw new Error(e.message || 'Network error');
  }
  let data = null;
  const ctype = res.headers.get('content-type') || '';
  if (ctype.includes('application/json')) {
    try { data = await res.json(); } catch { /* ignore */ }
  }
  if (!res.ok) {
    const detail = data?.detail ?? res.statusText;
    const err = new Error(formatErrorDetail(detail));
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return data;
}

export const api = {
  // auth
  register: (payload) => request('/auth/register', { method: 'POST', body: payload }),
  login:    (payload) => request('/auth/login',    { method: 'POST', body: payload }),
  logout:   () => request('/auth/logout', { method: 'POST' }),
  me:       () => request('/auth/me'),
  updateProfile:  (body) => request('/auth/me', { method: 'PATCH', body }),
  changeEmail:    (body) => request('/auth/change-email', { method: 'POST', body }),
  changePassword: (body) => request('/auth/change-password', { method: 'POST', body }),
  setMyPicture:   (dataUrl) => request('/auth/me/picture', { method: 'POST', body: { data_url: dataUrl } }),
  clearMyPicture: () => request('/auth/me/picture', { method: 'DELETE' }),

  // marketplace
  getCatalog:     () => request('/marketplace/catalog'),
  setCatalog:     (spriteId, body) => request(`/marketplace/catalog/${spriteId}`, { method: 'PUT', body }),
  createOffer:    (body) => request('/marketplace/offers', { method: 'POST', body }),
  listMyOffers:   () => request('/marketplace/offers/mine'),
  listAllOffers:  (status) => request(`/marketplace/offers${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  acceptOffer:    (id, body) => request(`/marketplace/offers/${id}/accept`, { method: 'POST', body }),
  declineOffer:   (id, body) => request(`/marketplace/offers/${id}/decline`, { method: 'POST', body }),
  deleteOffer:    (id) => request(`/marketplace/offers/${id}`, { method: 'DELETE' }),

  // messages (chat per offer)
  listMessages: (offerId) => request(`/marketplace/offers/${offerId}/messages`),
  sendMessage:  (offerId, body) => request(`/marketplace/offers/${offerId}/messages`, { method: 'POST', body: { body } }),

  // notifications
  listNotifications: () => request('/marketplace/notifications'),
  markRead:          (id) => request(`/marketplace/notifications/${id}/read`, { method: 'POST' }),
  markAllRead:       () => request('/marketplace/notifications/read-all', { method: 'POST' }),

  // users + trade requests
  lookupUser:       (username) => request(`/users/lookup?username=${encodeURIComponent(username)}`),
  getPublicProfile: (username) => request(`/users/public/${encodeURIComponent(username)}`),
  getTopTraders:    (limit = 12) => request(`/users/top-traders?limit=${limit}`),
  searchUsers:      (q) => request(`/users/search?q=${encodeURIComponent(q)}`),
  createTradeReq:   (body) => request('/trade-requests', { method: 'POST', body }),
  listMyTrades:     () => request('/trade-requests/mine'),
  acceptTradeReq:   (id, body = {}) => request(`/trade-requests/${id}/accept`, { method: 'POST', body }),
  declineTradeReq:  (id, body = {}) => request(`/trade-requests/${id}/decline`, { method: 'POST', body }),
  cancelTradeReq:   (id) => request(`/trade-requests/${id}/cancel`, { method: 'POST' }),
  deleteTradeReq:   (id) => request(`/trade-requests/${id}`, { method: 'DELETE' }),

  // user sprite collection (cross-device sync)
  getMyCollection:    () => request('/collections/me'),
  putMyCollection:    (entries) => request('/collections/me', { method: 'PUT', body: { entries } }),
  mergeMyCollection:  (entries) => request('/collections/me/merge', { method: 'POST', body: { entries } }),
  resetMyCollection:  () => request('/collections/me', { method: 'DELETE' }),

  // support chat (dev inbox)
  sendSupportMessage:  (body) => request('/support/messages', { method: 'POST', body }),
  listSupportMessages: (threadKey) => request(`/support/messages${threadKey ? `?thread_key=${encodeURIComponent(threadKey)}` : ''}`),
  listSupportThreads:  () => request('/support/threads'),
  getSupportThread:    (threadKey) => request(`/support/threads/${encodeURIComponent(threadKey)}/messages`),
  replySupportThread:  (threadKey, text) => request(`/support/threads/${encodeURIComponent(threadKey)}/reply`, { method: 'POST', body: { text } }),
  markSupportRead:     (threadKey) => request(`/support/threads/${encodeURIComponent(threadKey)}/mark-read`, { method: 'POST' }),

  // creators — bonus lottery entries for sharing
  submitCreatorPost:      (body) => request('/creators/submissions', { method: 'POST', body }),
  listMyCreatorPosts:     () => request('/creators/submissions/mine'),
  getMyCreatorBonus:      () => request('/creators/bonus/mine'),
  listCreatorSubmissions: (status) => request(`/creators/submissions${status ? `?status=${status}` : ''}`),
  approveCreator:         (id, body = {}) => request(`/creators/submissions/${id}/approve`, { method: 'POST', body }),
  rejectCreator:          (id, body = {}) => request(`/creators/submissions/${id}/reject`, { method: 'POST', body }),

  // admin — users list
  adminListUsers: () => request('/auth/admin/users'),
  adminDeleteUser: (id) => request(`/auth/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminListTrades: ({ status, user, limit = 200 } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (user) params.set('user', user);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return request(`/admin/trades${qs ? `?${qs}` : ''}`);
  },
  adminStaleTraders: (minHours = 24) => request(`/admin/trades/stale?min_hours=${minHours}`),

  // lottery
  getLotterySettings:  () => request('/lottery/settings'),
  updateLotterySettings: (body) => request('/lottery/settings', { method: 'PUT', body }),
  getMyLotteryEntries: () => request('/lottery/entries/mine'),
  getLotteryLeaderboard: (limit = 25) => request(`/lottery/leaderboard?limit=${limit}`),
  pickLotteryWinners:  (body) => request('/lottery/pick-winners', { method: 'POST', body }),
  listLotteryDraws:    (limit = 20) => request(`/lottery/draws?limit=${limit}`),
  featureLotteryDraw:  (id, featured) => request(`/lottery/draws/${encodeURIComponent(id)}`, { method: 'PATCH', body: { featured } }),
  deleteLotteryDraw:   (id) => request(`/lottery/draws/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getFeaturedDraw:     () => request('/lottery/featured-draw'),
  grantEntries:        (userId, body) => request(`/lottery/users/${encodeURIComponent(userId)}/grant`, { method: 'POST', body }),

  // peer-to-peer chat threaded onto an existing trade request
  listTradeMessages:   (tradeId) => request(`/trade-requests/${encodeURIComponent(tradeId)}/messages`),
  sendTradeMessage:    (tradeId, body) => request(`/trade-requests/${encodeURIComponent(tradeId)}/messages`, { method: 'POST', body: { body } }),

  // site-wide feature flags (intro splash toggle, etc.)
  siteSettings:        () => request('/site/settings'),
  updateSiteSettings:  (body) => request('/admin/site/settings', { method: 'PATCH', body }),

  // dynamic sprites auto-scraped from fortnite.gg
  listDynamicSprites:  () => request('/sprites/dynamic'),
  getSeasons:          () => request('/sprites/seasons'),
  adminGetSeasons:     () => request('/admin/seasons'),
  adminSetCurrentSeason: (season) => request('/admin/seasons/current', { method: 'PUT', body: { season } }),
  adminSetFamilySeason:  (family, season) => request('/admin/seasons/family', { method: 'PATCH', body: { family, season } }),
  adminResetFamilySeason: (family) => request(`/admin/seasons/family/${encodeURIComponent(family)}`, { method: 'DELETE' }),
  getFortniteSync:     () => request('/admin/fortnite-sync'),
  toggleFortniteSync:  (enabled) => request('/admin/fortnite-sync', { method: 'PATCH', body: { enabled } }),
  runFortniteSync:     () => request('/admin/fortnite-sync/run', { method: 'POST' }),
  importFortniteHtml:  (html) => request('/admin/fortnite-sync/import', { method: 'POST', body: { html } }),

  // merchandise
  listMerch:           ({ category, featured, limit } = {}) => {
    const q = new URLSearchParams();
    if (category) q.append('category', category);
    if (featured !== undefined) q.append('featured', featured);
    if (limit) q.append('limit', limit);
    return request('/merch/products' + (q.toString() ? `?${q}` : ''));
  },
  getMerchProduct:     (id) => request(`/merch/products/${id}`),
  adminImportMerchUrl: (url, category) => request('/merch/admin/import-url', { method: 'POST', body: { url, category } }),
  adminCreateMerch:    (product) => request('/merch/admin/products', { method: 'POST', body: product }),
  adminUpdateMerch:    (id, patch) => request(`/merch/admin/products/${id}`, { method: 'PATCH', body: patch }),
  adminDeleteMerch:    (id) => request(`/merch/admin/products/${id}`, { method: 'DELETE' }),
  adminSeed3dPrints:   () => request('/merch/admin/seed-3d-prints', { method: 'POST' }),
};

export { formatErrorDetail };
